import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle, Copy, ArrowRight, Rocket } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", issuedKey: data.issued_key, issuedKeys: data.issued_keys || [], lookup: data.lookup_key });
          refresh && refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed" });
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current > 15) {
        setState({ status: "timeout" });
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const copy = (k) => {
    navigator.clipboard.writeText(k);
    toast.success("Key copied");
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-lg text-center fadeup">
        {state.status === "checking" && (
          <p data-testid="payment-checking" className="font-mono-p text-cyan-400 text-sm tracking-[0.3em] animate-pulse">
            CONFIRMING PAYMENT...
          </p>
        )}

        {state.status === "paid" && (
          <div className="border border-cyan-400/30 bg-[#090A0C] p-10">
            <CheckCircle size={48} weight="duotone" className="text-cyan-400 mx-auto mb-6" />
            <h1 data-testid="payment-success" className="font-mono-p font-bold uppercase tracking-tight text-2xl">
              Payment Confirmed
            </h1>
            {state.issuedKeys && state.issuedKeys.length > 0 ? (
              <>
                <p className="mt-3 text-sm text-zinc-400">
                  {state.issuedKeys.length === 1
                    ? "Your license key has been issued. Activate it on your Plan page to lock it to your email:"
                    : `${state.issuedKeys.length} license keys have been issued. Activate one for yourself, then send the spares to your teammates from your Plan page:`}
                </p>
                <div className="mt-5 flex flex-col items-center gap-2">
                  {state.issuedKeys.map((k) => (
                    <button
                      key={k}
                      onClick={() => copy(k)}
                      data-testid="issued-key"
                      className="inline-flex items-center gap-2 font-mono-p text-cyan-300 text-lg tracking-wider border border-cyan-400/30 px-4 py-3 hover:bg-cyan-400/5 transition-colors drop-shadow-[0_0_8px_rgba(0,240,255,0.25)]"
                    >
                      {k} <Copy size={16} className="text-zinc-500" />
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-xs text-zinc-600">
                  Keys are single-use and lock permanently to the first email that activates them.
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm text-zinc-400 flex items-center justify-center gap-2">
                <Rocket size={16} className="text-amber-400" /> S.I.M.O.N. subscription active. See you at launch!
              </p>
            )}
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/dashboard" data-testid="go-dashboard" className="flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-5 py-3 font-bold hover:bg-cyan-300 transition-colors">
                Go to Dashboard <ArrowRight size={15} weight="bold" />
              </Link>
              <Link to="/plan" className="font-mono-p text-sm uppercase tracking-widest border border-zinc-700 px-5 py-3 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors">
                Manage Plan
              </Link>
            </div>
          </div>
        )}

        {["failed", "timeout", "error"].includes(state.status) && (
          <div className="border border-red-400/30 bg-[#090A0C] p-10">
            <h1 className="font-mono-p font-bold uppercase tracking-tight text-xl text-red-400">
              {state.status === "failed" ? "Payment Failed" : "Still Processing"}
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              {state.status === "failed"
                ? "Your payment didn't go through. Please try again."
                : "We couldn't confirm your payment yet. Check your Plan page in a moment."}
            </p>
            <Link to="/store" className="inline-block mt-6 font-mono-p text-sm uppercase tracking-widest border border-zinc-700 px-5 py-3 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors">
              Back to Store
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
